<template>
  <ApplicationAddable
    v-bind="$attrs"
    :text-item="$attrs.applicationType != 'owner-occupancy' ? translate('hakija') : translate('varaaja')"
    :min="Object.keys(existingApplicantIndexes).length >= 2 ? 2 : 1"
    :max="4"
    @update="$emit('update', $event)"
    :interactive="!preview"
  >
    <template #title="{index, total}" v-if="$attrs.applicationType != 'owner-occupancy'">
      {{sprintf(translate('Hakijan %s tiedot'), indexString(index, total))}}
    </template>
    <template #title="{ index, total }" v-if="$attrs.applicationType == 'owner-occupancy'">
        {{ sprintf(translate('Varaajan %s tiedot'), indexString(index, total)) }}
    </template>

    <template #title-description v-if="$attrs.applicationType != 'owner-occupancy'">
      {{translate('Täytä vähintään vaaditut kentät asianmukaisin tiedoin, jotta voimme käsitellä hakemuksesi'
      + ' mahdollisimman pikaisesti.')}}
    </template>
    <template #title-description v-if="$attrs.applicationType == 'owner-occupancy'">
      {{translate('Täytä vähintään vaaditut kentät asianmukaisin tiedoin, jotta voimme käsitellä varauksesi'
      + ' mahdollisimman pikaisesti.')}}
    </template>

    <template #default="{index: i}">
      <div class="form form--bg-lighter-green">

        <div class="form__title-wrapper">
          <div class="form__title">
              <h2 class="h3">
                {{translate('Hakijan tiedot')}}
              </h2>
          </div>
        </div>

        <div class="form__grid">

          <div class="form__row">
            <InputWrap
              class="form__col-4"
              data-cy="first-name"
              :label="translate('Etunimet')"
              :validations="vIter[i].FirstName"
              v-slot="{error}"
            >
              <InputText
                v-model="vIter[i].FirstName.$model"
                data-cy="first-name-input"
                :disabled="preview"
                :error="error"
              />
            </InputWrap>

            <InputWrap
              class="form__col-4"
              data-cy="last-name"
              :label="translate('Sukunimi')"
              :validations="vIter[i].LastName"
              v-slot="{error}"
            >
              <InputText
                v-model="vIter[i].LastName.$model"
                data-cy="last-name-input"
                :disabled="preview"
                :error="error"
              />
            </InputWrap>

            <InputWrap
              class="form__col-4"
              data-cy="ssn"
              :label="translate('Henkilötunnus')"
              :validations="vIter[i].Ssn"
              :info="ssnDisabled(i)
                ? ($attrs.applicationType != 'owner-occupancy' ? translate('Hakemuksen päähakijaa ei voi muuttaa.') : translate('Varauksen päähakijaa ei voi muuttaa'))
                : null"
              v-slot="{error}"
            >
              <InputText
                v-if="!ssnDisabled(i)"
                v-model="vIter[i].Ssn.$model"
                data-cy="ssn-input"
                :disabled="preview || ssnDisabled(i)"
                :error="error"
              />
              <span class="input-text" data-cy="ssn-input" v-else>
                <input class="input-text__input" autocomplete="off" type="hidden" data-cy="ssn-input" :value="vIter[i].Ssn.$model" />
                <!---->
                <span class="input-text__input" style="">{{ vIter[i].Ssn.$model.substring(0, vIter[i].Ssn.$model.length - 4) + '****'}}</span>
              </span>

            </InputWrap>

          </div>

          <div
            v-if="!vIter[i].usePrimaryApplicantAddress.$model"
            class="form__row"
          >
            <InputWrap
              class="form__col-6"
              data-cy="street-address"
              :label="translate('Lähiosoite')"
              :validations="vIter[i].Address"
              v-slot="{error}"
            >
              <InputText
                v-model="vIter[i].Address.$model"
                data-cy="street-address-input"
                :disabled="preview"
                :error="error"
              />
            </InputWrap>

            <InputWrap
              class="form__col-3"
              data-cy="postal-code"
              :label="translate('Postinumero')"
              :validations="vIter[i].PostalCode"
              v-slot="{error}"
            >
              <InputText
                v-model="vIter[i].PostalCode.$model"
                data-cy="postal-code-input"
                :disabled="preview"
                :error="error"
              />
            </InputWrap>

            <InputWrap
              class="form__col-3"
              data-cy="post-office"
              :label="translate('Postitoimipaikka')"
              :validations="vIter[i].City"
              v-slot="{error}"
            >
              <InputText
                v-model="vIter[i].City.$model"
                data-cy="post-office-input"
                :disabled="preview"
                :error="error"
              />
            </InputWrap>
          </div>

          <div
            v-if="i > 0"
            class="form__row"
          >
            <div class="form__col">
              <InputCheckbox
                v-model="vIter[i].usePrimaryApplicantAddress.$model"
                :disabled="preview"
                wrapperClasses="input-wrapper input-wrapper--flex"
              >
                {{translate('Osoite sama kuin päähakijalla')}}
              </InputCheckbox>
            </div>
          </div>

          <div class="form__row">
            <InputWrap
              class="form__col"
              data-cy="contact-language"
              :label="translate('Asiointikieli')"
              :wrap-label="false"
              v-slot="{error}"
              :validations="vIter[i].ContactLanguage"
            >
              <InputSelect
                v-model="vIter[i].ContactLanguage.$model"
                :error="error"
                :allowEmpty="false"
                :value="[
                  {label: 'Suomi', value: 'fi'}, // Finland
                ]"
                :options="[
                  {label: 'Suomi', value: 'fi'}, // Finland
                  {label: 'Ruotsi', value: 'sv'}, // Sweden
                  {label: 'Englanti', value: 'en'}, // English
                ]"
                simple-value
              />
            </InputWrap>
          </div>


          <div class="form__row">
            <div
              v-if="!preview && $attrs.applicationType != 'owner-occupancy'"
              class="form__col print-hide"
            >
                {{ translate('Tarvitsemme hakemustanne varten puhelinnumeron tai sähköpostiosoitteen. Voit myös antaa'
                  + ' molemmat.')}}
            </div>
            <div
              v-if="!preview && $attrs.applicationType == 'owner-occupancy'"
              class="form__col print-hide"
            >
                {{ translate('Tarvitsemme varaustanne varten puhelinnumeron tai sähköpostiosoitteen. Voit myös antaa'
                  + ' molemmat.')}}
            </div>

            <InputWrap
              class="form__col-3"
              data-cy="phone-number-country-code"
              :label="translate('Maakoodi')"
              :wrap-label="false"
              v-slot="{error}"
              :validations="vIter[i].PhoneCountryCode"
            >
              <InputSelect
                v-model="vIter[i].PhoneCountryCode.$model"
                :error="error"
                :allowEmpty="false"
                :value="[
                    {label: '+358', value: '+358'}, // Finland
                ]"
                :options="[
                  {label: '+358', value: '+358'}, // Finland
                  {label: '+372', value: '+372'}, // Estonia
                  {label: '+46', value: '+46'}, // Sweden
                  {label: '+44', value: '+44'}, // UK
                  {label: '+49', value: '+49'}, // Germany
                  {label: '+1', value: '+1'}, // USA
                  {label: '-----------', value: '', $isDisabled: true}, // Disabled divider
                  ...countryCodes // All country codes
                ]"
                simple-value
              />
            </InputWrap>

            <InputWrap
              class="form__col-6"
              data-cy="phone-number"
              :label="translate('Puhelinnumero')"
              :validations="vIter[i].Phone"
              :validation-messages="{required: 'Puhelinnumero vaaditaan.'}"
              v-slot="{error}"
            >
              <InputText
                v-model="vIter[i].Phone.$model"
                data-cy="phone-number-input"
                :disabled="preview"
                :error="error"
              />
              <div style="font-size: 14px; line-height: 15px; margin-top: 6px;">
                <span style="user-select: none; border: 1px solid orange; border-radius: 50%; width: 20px; height: 20px; display: inline-block; text-align: center; line-height: 20px; font-size: 14px; margin-right: 4px; font-weight: bold; position: relative; top: 1px;">!</span> {{ translate("Syötä puhelinnumero muodossa 0401234567") }}
              </div>
            </InputWrap>
          </div>

          <div class="form__row">
            <InputWrap
              class="form__col-6"
              data-cy="email"
              :label="translate('Sähköposti')"
              :validations="vIter[i].Email"
              v-slot="{error}"
            >
              <InputText
                v-model="vIter[i].Email.$model"
                data-cy="email-input"
                type="email"
                :disabled="preview"
                :error="error"
              />
            </InputWrap>

            <InputWrap
              v-if="!preview"
              class="form__col-6 print-hide"
              data-cy="verify-email"
              :label="`${translate('Sähköposti uudelleen')} *`"
              :validations="vIter[i].emailAgain"
              v-slot="{error}"
            >
              <InputText
                v-model="vIter[i].emailAgain.$model"
                data-cy="verify-email-input"
                type="email"
                :disabled="preview"
                :error="error"
              />
            </InputWrap>
          </div>

          <slot :vApplicant="vIter[i]" :index="i"/>

          <div
            v-if="i === 0"
            class="form__row"
          >
            <div class="form__col">
              <InputCheckbox
                wrapperClasses="input-wrapper input-wrapper--flex"
                v-model="vIter[i].MarketingAllowed.$model"
                data-cy="marketing-allowed"
                :disabled="preview"
              >
                {{translate('Haluan tilata Avain Asuntojen uutiskirjeen ja saada sähköpostiini tietoa asunnoista,'
                + ' palveluista ja tapahtumista. (Tilauksen voi peruuttaa koska tahansa uutiskirjeen linkistä.)')}}
              </InputCheckbox>
            </div>
          </div>

        </div>
      </div>
    </template>
  </ApplicationAddable>
</template>

<script>
  import {sprintf} from 'sprintf-js';

  import { countryCodes } from "../../helpers/countries"
  import vueBind from '../../lib/vueBind';
  import utils from '../mixins/utils';
  import ApplicationAddable from './ApplicationAddable';
  import InputWrap from './InputWrap';
  import InputText from './InputText';
  import InputCheckbox from './InputCheckbox';
  import InputSelect from './InputSelect.vue';

  export default {
    components: {
      ApplicationAddable,
      InputWrap,
      InputText,
      InputCheckbox,
      InputSelect,
    },
    mixins: [
      utils.translate,
    ],
    data() {
      return {
        countryCodes,
      }
    },
    props: {
      vIter: {
        type: Object,
        required: true,
      },
      preview: {
        type: Boolean,
        required: true,
      },
      isExistingApplication: {
        type: Boolean,
        required: true,
      },
      existingApplicantIndexes: {
        type: Object,
        required: true,
        default() { return {} },
      }
    },
    updated() {
      // When form (applicants) is updated, check if a new applicant has been added -> update AddedNewApplicant field = 1
      if (Object.keys(this.vIter).length > Object.keys(this.existingApplicantIndexes).length &&
        Object.keys(this.existingApplicantIndexes).length > 0) {
        this.vIter[Object.keys(this.vIter).length-1].AddedNewApplicant.$model = "1";
      }
    },
    methods: {
      sprintf,
      indexString(index, total) {
        if(this.$attrs.applicationType === 'owner-occupancy') {
          return index === 0 ? `` : `(${index + 1}/${total})`;
        } else {
          return index === 0 ? `(${this.translate('Päähakija')})` : `(${index + 1}/${total})`;
        }

      },
      ssnDisabled(index) {
        return (
          this.isExistingApplication
          &&
          (index === 0 || (index === 1 && this.existingApplicantIndexes[1] === true))
        )
      },
    },
  };
</script>
